import React from 'react';
import { Box, Button, ButtonGroup, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { sanitizeRichTextHtml } from './utils';

type Props = {
  value: string;
  onChange: (_value: string) => void;
  placeholder?: string;
};

const EMPTY_EDITOR_HTML = '';

export default function CustomRichTextEditor({
  value,
  onChange,
  placeholder,
}: Readonly<Props>) {
  const { t } = useTranslation('lca', {
    keyPrefix: 'reportBuilder.richTextEditor',
  });
  const editorRef = React.useRef<HTMLDivElement | null>(null);
  const resolvedPlaceholder = placeholder || t('placeholder');

  React.useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML === value) return;
    editorRef.current.innerHTML = value || EMPTY_EDITOR_HTML;
  }, [value]);

  const emitChange = React.useCallback(() => {
    if (!editorRef.current) return;

    const sanitized = sanitizeRichTextHtml(editorRef.current.innerHTML);
    if (editorRef.current.innerHTML !== sanitized) {
      editorRef.current.innerHTML = sanitized;
    }
    onChange(sanitized);
  }, [onChange]);

  const applyCommand = (command: string, commandValue?: string) => {
    if (!editorRef.current) return;

    editorRef.current.focus();
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  return (
    <Stack spacing={1}>
      <ButtonGroup size="small" variant="outlined">
        <Button onClick={() => applyCommand('bold')}>B</Button>
        <Button onClick={() => applyCommand('italic')}>I</Button>
        <Button onClick={() => applyCommand('formatBlock', 'h2')}>H2</Button>
        <Button onClick={() => applyCommand('insertUnorderedList')}>
          {t('bulletedList')}
        </Button>
        <Button onClick={() => applyCommand('insertOrderedList')}>
          {t('numberedList')}
        </Button>
        <Button onClick={() => applyCommand('formatBlock', 'blockquote')}>
          {t('quote')}
        </Button>
        <Button
          onClick={() => {
            const href = window.prompt(t('enterUrl'));
            if (!href) return;
            applyCommand('createLink', href);
          }}
        >
          {t('link')}
        </Button>
        <Button onClick={() => applyCommand('removeFormat')}>
          {t('clear')}
        </Button>
      </ButtonGroup>

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        sx={{
          minHeight: 180,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          backgroundColor: '#fff',
          outline: 'none',
          '&:empty:before': {
            content: `"${resolvedPlaceholder}"`,
            color: 'text.disabled',
          },
          '& p, & div': {
            my: 0,
            mb: 1,
          },
          '& h2': {
            fontSize: 20,
            lineHeight: 1.3,
            mt: 0,
            mb: 1,
            color: 'primary.main',
          },
          '& ul, & ol': {
            pl: 3,
            my: 0,
            mb: 1,
          },
          '& blockquote': {
            m: 0,
            pl: 1.5,
            borderLeft: '3px solid',
            borderColor: 'primary.light',
            color: 'text.secondary',
          },
          '& a': {
            color: 'primary.main',
          },
        }}
      />

      <Typography variant="caption" color="text.secondary">
        {t('supportedFormatting')}
      </Typography>
    </Stack>
  );
}
